/* eslint-disable import/no-deprecated */
import { OmitStrict } from '@fullstory/typeutils';
import { handleInteractiveKeydown } from '@fullstory/util/a11y';
import { classNames } from '@fullstory/util/fmt';
import React from 'react';
import { Button } from '../../components/Button';
import { TextInput } from '../../components/TextInput';
import { Box } from '../../legacy/Box';
import { Icon } from '../Icon';
import { listNav } from '../ListNav';
import {
  DeprecatedBaseOptionMenuProps,
  DeprecatedOptionMenu,
  DeprecatedOptionMenuProps,
} from '../OptionMenu';
import { Typography } from '../Typography';
import css from './select.module.scss';

/**
 * @deprecated
 */
interface SelectProps<T>
  extends DeprecatedBaseOptionMenuProps<T>,
    Pick<React.ComponentProps<typeof Button>, 'size' | 'variant' | 'disabled'> {
  /** The current value of the select. */
  value: T | null;
  /**
   * Optional function for rendering an option.
   * If not provided, the optionText will be used to render the option.
   */
  renderSelected?: (opt: T) => React.ReactNode;
  /** Placeholder string for when no option is selected. */
  placeholder: string;
  /** Placeholder string for the filter input. */
  filterPlaceholder: string;
  /** If true, the select dropdown will stay open after an item has been selected */
  stayOpen: boolean;
  /** If true, hide the search box. */
  hideSearch?: boolean;
  // if true, the component will be marked as invalid
  invalid?: boolean;
  // Create select dropdown in open state
  startOpen?: boolean;
  /** Callback for when open state is set to false */
  onClose?: () => void;
  // locked prevents opening or changing the selector
  locked?: boolean;
  // if intent is set to 'settings' then it will match the blue color theme of the settings pages
  // searchCategory will add a minimal underline effect.
  intent?: 'settings' | 'searchCategoryPrimary' | 'searchCategoryDependent';
  /** if true, display the search icon in the search box */
  displaySearchIcon?: boolean;
  // data attribute name for testing
  testId?: string;
}

interface SelectState {
  /** Whether the popup menu is currently open. */
  open: boolean;

  // Anchor ref used for the popup - set async on first render so we delay rendering dependents until after this is set.
  selectRefCurrent: HTMLElement | null;
}

/**
 * @deprecated
 */
class Select<T> extends React.Component<SelectProps<T>, SelectState> {
  static readonly defaultProps = {
    placeholder: 'Select an option...',
    stayOpen: false,
    variant: 'primary',
    size: 'medium',
    filterPlaceholder: 'Choose an option...',
    matchWidth: true,
    startOpen: false,
    locked: false,
    invalid: false,
    disabled: false,
    displaySearchIcon: false,
    testId: 'select',
  };

  state: SelectState = {
    open: this.props.startOpen!,
    selectRefCurrent: null, // Element for the select element, used to position the popup menu.
  };

  private setRef = (element: HTMLElement | null) => {
    this.setState({
      selectRefCurrent: element,
    });
  };

  render() {
    const textSize = {
      'x-small': 'body3',
      small: 'body3',
      medium: 'body2',
      large: 'body1',
      'x-large': 'body1',
    } as const;

    let selectedValue: React.ReactNode;
    if (this.props.value) {
      // If we have a current value, render it using the custom render method if provided.
      // If not provided, fall back on the text representation.
      if (this.props.renderSelected) {
        selectedValue = this.props.renderSelected(this.props.value);
      } else {
        selectedValue = this.props.optionText(this.props.value);
      }
    } else {
      // No current value. Show the placeholder string.
      selectedValue = (
        <span>
          <Typography
            variant={textSize[this.props.size!]}
            color={this.props.variant === 'fill-primary' ? 'neutrals-n1' : undefined}
          >
            {this.props.placeholder}
          </Typography>
        </span>
      );
    }

    const className = classNames('selectComponent', css.selectComponent, this.props.className, {
      open: this.state.open,
    });

    // Return a fragment because the menu is rendered through a portal,
    // so it isn't going to be a child of this component in the DOM.
    return (
      <>
        <div
          role="button"
          tabIndex={0}
          onKeyDown={handleInteractiveKeydown(this.toggle)}
          data-testid={this.props.testId}
          className={className}
          onClick={this.toggle}
          ref={this.setRef}
        >
          <Button
            variant={this.props.variant}
            iconRight="chevron-down"
            className={classNames(this.props.className, 'arrowIcon', {
              open: this.state.open,
              searchCategoryPrimary: this.props.intent === 'searchCategoryPrimary',
              searchCategoryDependent: this.props.intent === 'searchCategoryDependent',
              [css.error]: this.props.invalid!,
              [css.locked]: this.props.locked!,
              [css.settings]: this.props.intent === 'settings',
              [css.searchCategoryDependent]: this.props.intent === 'searchCategoryDependent',
            })}
            size={this.props.size}
            disabled={this.props.disabled}
          >
            {selectedValue}
          </Button>
        </div>
        {this.state.open && !this.props.locked && this.state.selectRefCurrent && (
          <SelectMenu<T>
            size={this.props.size}
            className={
              this.props.className
                ? this.props.className
                    .split(' ')
                    .map(d => `${d}-popup`)
                    .join(' ')
                : undefined
            } // What is even going on here?
            itemClassName={this.props.itemClassName}
            anchor={this.state.selectRefCurrent}
            placement={this.props.placement}
            value={this.props.value}
            options={this.props.options}
            optionId={this.props.optionId}
            optionText={this.props.optionText}
            onSelect={this.handleOptionClick}
            onCloseSignal={this.close}
            renderBelow={this.props.renderBelow}
            renderAbove={this.props.renderAbove}
            renderItem={this.props.renderItem}
            filterPlaceholder={this.props.filterPlaceholder}
            hideSearch={this.props.hideSearch}
            matchWidth={this.props.matchWidth}
            maxHeight={this.props.maxHeight}
            intent={this.props.intent}
            categoryOps={this.props.categoryOps}
            displaySearchIcon={this.props.displaySearchIcon}
          />
        )}
      </>
    );
  }

  private toggle = () => {
    if (this.props.locked || this.props.disabled) {
      return;
    }
    this.setState(prev => ({ open: !prev.open }));
  };

  close = () => {
    this.setState({ open: false }, this.props.onClose);
  };

  private handleOptionClick = (opt: T) => {
    if (!this.props.stayOpen) {
      this.close();
    }
    this.props.onSelect(opt);
  };
}

interface SelectMenuProps<T>
  extends OmitStrict<DeprecatedOptionMenuProps<T>, 'focusedOptionId'>,
    Pick<
      SelectProps<T>,
      'size' | 'value' | 'filterPlaceholder' | 'hideSearch' | 'intent' | 'displaySearchIcon'
    > {}

interface SelectMenuState {
  /** The current text to filter the options against. */
  filterText: string;
  /** ID of the currently focused menu option. */
  focusedOptionId: string | null;
}

class SelectMenu<T> extends React.Component<SelectMenuProps<T>, SelectMenuState> {
  constructor(props: SelectMenuProps<T>) {
    super(props);

    let focusedOptionId = null;
    if (this.props.value) {
      const valueIndex = this.props.options.findIndex(value => this.props.value === value);
      focusedOptionId = valueIndex ? this.props.optionId(this.props.value, valueIndex) : null;
    }

    this.state = { filterText: '', focusedOptionId };
  }

  componentDidUpdate(
    prevProps: Readonly<SelectMenuProps<T>>,
    prevState: Readonly<SelectMenuState>,
  ) {
    // There are two things we'd like to do when the filter text changes:
    // If there are no options that match the filter, we want to clear the focused option.
    // If there is only one option that matches the filter, we want to focus that option.

    if (this.state.filterText == prevState.filterText) {
      // Same filter text as before, nothing we need to do.
      return;
    }

    const filteredOpts = this.filteredOptions;
    if (filteredOpts.length == 0) {
      this.setState({ focusedOptionId: null });
    } else if (filteredOpts.length == 1 && filteredOpts[0]) {
      this.setState({ focusedOptionId: filteredOpts[0].id });
    }
  }

  render() {
    const filteredOptions = this.filteredOptions;
    return (
      <DeprecatedOptionMenu
        data-component="SelectMenu"
        {...this.props}
        options={filteredOptions.map(o => o.opt)}
        focusedOptionId={this.state.focusedOptionId}
        renderAbove={this.props.renderAbove}
        renderSearch={this.renderSearch()}
        renderItem={this.props.renderItem}
        renderBelow={this.props.renderBelow}
        matchWidth={this.props.matchWidth}
        maxHeight={this.props.maxHeight}
        hideSearch={this.props.hideSearch}
      />
    );
  }
  renderSearch = (): (() => React.ReactNode) => () =>
    this.props.hideSearch ? null : (
      <AutofocusedTextInput
        size={this.props.size}
        type="text"
        className={classNames(css.selectTextInput, 'selectTextInput')}
        value={this.state.filterText}
        onChange={this.handleFilterChange}
        onKeyDown={this.handleKeydown}
        placeholder={this.props.filterPlaceholder}
        leftAdornment={
          this.props.displaySearchIcon && (
            <Box marginLeft={8}>
              <Icon name="search" color="pink-base" size={16} />
            </Box>
          )
        }
      />
    );

  private get filteredOptions(): { opt: T; text: string; id: string }[] {
    const filterText = this.state.filterText.toLowerCase();
    return this.props.options
      .map((opt, i) => {
        const id = this.props.optionId(opt, i);
        const text = this.props.optionText(opt);
        return { opt, id, text };
      })
      .filter(({ text }) => text.toLowerCase().includes(filterText));
  }

  private handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ filterText: e.target.value });
  };

  get navItems() {
    return this.filteredOptions;
  }

  get focusedOptionId(): string | null {
    if (!this.state || !this.state.focusedOptionId) {
      return null;
    }
    return this.state.focusedOptionId;
  }

  private handleKeydown = listNav(this, {
    arrowKey: id => this.setState({ focusedOptionId: id }),
    enter: id => {
      const opt = this.props.options.find((opt, i) => this.props.optionId(opt, i) == id);
      if (opt) {
        this.props.onSelect(opt);
      }
    },
  });
}

const AutofocusedTextInput: React.FC<React.ComponentProps<typeof TextInput>> = props => {
  const ref = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => ref.current?.focus(), []);
  return <TextInput data-component="AutofocusedTextInput" ref={ref} {...props} />;
};

export { Select };
export type { SelectProps };
